import React, { useRef, useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { calculateResizeHeight, validateHeight } from './utils';
import { KeyMap } from '../KeyBinding/utils';

const useStyles = makeStyles()(theme => ({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: '1px',
    margin: '-1px',
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: '1px',
    whiteSpace: 'nowrap',
  },
  verticalResizer: {
    backgroundClip: 'content-box',
    backgroundColor: theme.palette.divider,
    cursor: 'ns-resize',
    height: '1px',
    left: 0,
    paddingTop: theme.spacing(6),
    boxSizing: 'content-box',
    width: '100%',
    zIndex: 1,
    '&:hover, &:focus-visible, &.outline-accent': {
      /**
       * This was done because background-clip: 'content-box' + padding are being used
       * to increase the interactive area of the horizontal rule. Outline-offset does not allow you
       * to manually set the left/right/top/bottom values.
       */
      backgroundColor: theme.mixins.palette.accents.purple2,
      outline: 'unset',
      height: '3px',
    },
  },
  container: {
    position: 'relative',
    '&.use-transition': {
      transition: theme.transitions.create('height', {
        easing: 'ease-in',
        duration: '0.1s',
      }),
    },
  },
}));

// Values used by keyboard functions & smoothing while dragging
export enum MovementY {
  up = -20,
  down = 20,
}

export type ResizeData = {
  keyUp?: boolean;
  mouseDown?: boolean;
  mouseUp?: boolean;
  movementY?: MovementY.up | MovementY.down | number;
  toMaxHeight?: boolean;
  toMinHeight?: boolean;
  toggle?: boolean;
  touchEnd?: boolean;
  touchStart?: boolean;
};

interface ResizerProps {
  constraints: Constraints;
  height: number;
  onResize: (resizeData: ResizeData) => void;
}

/**
 * Custom slider control responsible for adjusting the height of the editor. Resizing can be achieved
 * through mouse or touch interactions by dragging up or down, or by utilizing keyboard controls.
 *
 * To ensure accessibility, this custom slider leverages ARIA roles, attributes, and best practices
 * to make the functionality comprehensible and usable to assistive technologies.
 *
 * @see http://www.w3.org/WAI/ARIA/apg/patterns/slider/
 */
export const Resizer = ({ constraints, height, onResize }: ResizerProps) => {
  const { classes, cx } = useStyles();
  const [isUsingDirectInteraction, setIsUsingDirectInteraction] =
    useState(false);
  const touchStartPosition = useRef<number>(0);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsUsingDirectInteraction(true);
    touchStartPosition.current = e.touches[0].clientY;
  };
  const handleMouseDown = () => setIsUsingDirectInteraction(true);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Preventing default behavior so that the element does not lose focus
    if (
      (
        [
          KeyMap.ArrowUp,
          KeyMap.ArrowDown,
          KeyMap.Home,
          KeyMap.End,
          KeyMap.Enter,
        ] as Array<string>
      ).includes(e.key)
    ) {
      e.preventDefault();
    }

    switch (e.key) {
      case KeyMap.ArrowUp:
        onResize({ movementY: MovementY.up });
        break;
      case KeyMap.ArrowDown:
        onResize({ movementY: MovementY.down });
        break;
      case KeyMap.Home:
        onResize({ toMinHeight: true });
        break;
      case KeyMap.End:
        onResize({ toMaxHeight: true });
        break;
      case KeyMap.Enter:
        onResize({ toggle: true });
        break;
      default:
    }
  };
  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (Object.values<string>(KeyMap).includes(e.key)) {
      onResize({ keyUp: true });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) =>
      onResize({ mouseDown: true, movementY: e.movementY });

    // For touch events, we can get the movement & direction by looking at the origin,
    // i.e. touch start, position then subtracting it from the touch move position
    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartPosition.current) {
        const newPosition = e.changedTouches[0].clientY;
        // Normalizing the delta to be -20 > delta < 20
        // to reduce huge swings in values
        const delta =
          (newPosition - touchStartPosition.current) %
          (Math.abs(MovementY.down) + 1);
        onResize({ touchStart: true, movementY: delta });
      }
    };
    const handleMouseUp = () => {
      setIsUsingDirectInteraction(false);
      onResize({ mouseUp: true });
    };
    const handleTouchEnd = () => {
      setIsUsingDirectInteraction(false);
      onResize({ touchEnd: true });
    };

    if (isUsingDirectInteraction) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('touchmove', handleTouchMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchend', handleTouchEnd);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isUsingDirectInteraction, onResize]);

  return (
    <>
      <span id="resizerLabel" className={classes.visuallyHidden}>
        Adjust editor height
      </span>
      <p id="resizerDescription" className={classes.visuallyHidden}>
        Use arrow keys, home, end, to resize the editor. Use enter to toggle
        between min and the previous height.
      </p>
      <div
        tabIndex={0}
        id="editor-resizer"
        role="slider"
        aria-valuemax={constraints.maxHeight}
        aria-valuemin={constraints.minHeight}
        aria-valuenow={height}
        aria-labelledby="resizerLabel"
        aria-describedby="resizerDescription"
        className={cx(
          classes.verticalResizer,
          isUsingDirectInteraction && 'outline-accent'
        )}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      />
    </>
  );
};

export interface Constraints {
  minHeight: number;
  maxHeight: number;
}

export interface OnResizeData extends ResizeData {
  height: number;
}

interface ResizableBoxProps {
  constraints: Constraints;
  children: React.ReactElement;
  className?: string;
  initialHeight?: number | null;
  onResize?: (resizeData: OnResizeData) => void;
}

/**
 * A container that utilizes the Resizer in order to update the height accordingly.
 */
export const ResizableBox = ({
  constraints,
  children,
  className = '',
  initialHeight = undefined,
  onResize = () => {},
}: ResizableBoxProps) => {
  const { classes, cx } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const { minHeight, maxHeight } = constraints;
  const [prevHeight, setPrevHeight] = useState<number>(minHeight);
  const [useTransition, setUseTransition] = useState<boolean>(true);
  const [initialHeightSet, setInitialHeightSet] = useState<boolean>(false);

  /**
   * If initialHeight is not null and valid (i.e. between the constraints)
   * then set the height of the container without the transition.
   */
  useEffect(() => {
    if (!containerRef.current) return;

    const validInitialHeight = validateHeight(
      initialHeight,
      minHeight,
      maxHeight
    );

    if (validInitialHeight && !initialHeightSet) {
      setInitialHeightSet(true);
      setUseTransition(false);
      containerRef.current.style.height = `${initialHeight}px`;
    }
  }, [initialHeight, initialHeightSet, minHeight, maxHeight]);

  const handleResize = useCallback(
    (resizeData: ResizeData) => {
      const container = containerRef.current;
      if (!container) return;

      let newHeight;
      const { height } = container.getBoundingClientRect();
      const { touchStart, mouseDown, toggle } = resizeData;

      // Disable transition on mouse/touch interactions
      setUseTransition(!(mouseDown || touchStart));

      if (toggle) {
        // Toggle between collapsed and previous height
        newHeight = height === minHeight ? prevHeight : minHeight;
      } else {
        // Calculate new height and set it
        newHeight = calculateResizeHeight({
          currentHeight: height,
          constraints: { minHeight, maxHeight },
          resizeData,
        });
      }

      newHeight = Math.floor(newHeight);
      container.style.height = `${newHeight}px`;
      // set the prevHeight and call onResize with the newHeight
      setPrevHeight(height);
      onResize(Object.assign(resizeData, { height: newHeight }));
    },
    [
      containerRef,
      minHeight,
      maxHeight,
      onResize,
      prevHeight,
      setPrevHeight,
      setUseTransition,
    ]
  );

  return (
    <div
      className={cx(
        classes.container,
        className,
        useTransition && 'use-transition'
      )}
      ref={containerRef}
      style={{ height: minHeight, minHeight, maxHeight }}
    >
      {children}
      <Resizer
        onResize={handleResize}
        constraints={constraints}
        height={prevHeight}
      />
    </div>
  );
};

export default ResizableBox;
